import { Grid, Box, Typography, Container } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import MissingContent from '../../../../components/shared/errorMessages/MissingContent';
import { ChildTileData } from '../../types/CMSTypes';

interface CMSConsumerApplicationsGridProps {
  childrenData: ChildTileData[];
  spacingValue?: number;
  marginTop?: number;
  marginBottom?: number;
  isHomeware?: boolean; // Special handling for homeware categories
  onItemClick?: (itemGroupName: string) => void; // For scroll functionality
  enableScrollMode?: boolean; // Toggle between navigation and scroll mode
}

const CMSConsumerApplicationsGrid: React.FC<CMSConsumerApplicationsGridProps> = ({
                                                                                   childrenData,
                                                                                   spacingValue = 4,
                                                                                   marginTop = 5,
                                                                                   marginBottom = 5,
                                                                                   isHomeware = false,
                                                                                   onItemClick,
                                                                                   enableScrollMode = false
                                                                                 }) => {
  const navigate = useNavigate();

  // ** Application Mapping for Fabric **
  const applicationMapping: { [key: string]: string[] } = {
    'Curtaining': ['Curtaining', 'Dual Purpose'],
    'Dual Purpose': ['Dual Purpose'],
    'Upholstery': ['Upholstery', 'Dual Purpose'],
  };

  // Function to format the display text
  const formatDisplayText = (applicationName: string, itemGroupName: string, categoryName: string) => {
    // For homeware, prefer category name over application
    let text = isHomeware ? (categoryName || applicationName || itemGroupName) : (applicationName || categoryName || itemGroupName);

    // Handle special cases
    if (text === "throws_quilts_and_furs") {
      return "Throws, Quilts and Furs";
    }

    if (text === "wall decor") {
      return "Wall Decor";
    }

    // Default behavior: replace underscores with spaces and capitalize
    return text.replace(/_/g, " ");
  };

  // Handle navigation based on category type - Consumer specific logic
  const handleNavigate = (child: ChildTileData) => {
    console.log('Consumer navigating with child data:', child); // Debug log

    const { category_type, item_group, application, category_name } = child;

    switch (category_type) {
      case 'application':
        // Consumer-specific application navigation with mapping
        if (item_group === '005' && application) {
          // Fabric applications - use mapping for query params
          const applications = applicationMapping[application] || [application];
          const searchParams = new URLSearchParams();
          applications.forEach((app) => searchParams.append('application', app));

          const url = `/item_group/005/application/${application.toLowerCase()}?${searchParams.toString()}`;
          console.log('Consumer application navigation:', url);
          navigate(url);
        } else if (item_group && application) {
          // Other applications - standard navigation
          const url = `/item_group/${encodeURIComponent(item_group)}/application/${encodeURIComponent(application)}`;
          console.log('Consumer application navigation:', url);
          navigate(url);
        }
        break;

      case 'item_group':
        // Navigate to item group page
        if (item_group) {
          const url = `/item_group/${encodeURIComponent(item_group)}`;
          console.log('Consumer item group navigation:', url);
          navigate(url);
        }
        break;

      case 'collection':
        // Navigate to collection page
        if (item_group && child.collection) {
          const url = `/item_group/${encodeURIComponent(item_group)}/collection/${encodeURIComponent(child.collection)}`;
          console.log('Consumer collection navigation:', url);
          navigate(url);
        }
        break;

      case 'brand':
        // Navigate to brand page
        if (child.brand) {
          const url = `/brand/${encodeURIComponent(child.brand)}`;
          console.log('Consumer brand navigation:', url);
          navigate(url);
        }
        break;

      default:
        // Fallback navigation based on available data
        if (item_group === '005' && application) {
          // Special handling for fabric
          const applications = applicationMapping[application] || [application];
          const searchParams = new URLSearchParams();
          applications.forEach((app) => searchParams.append('application', app));

          const url = `/item_group/005/application/${application.toLowerCase()}?${searchParams.toString()}`;
          console.log('Consumer fallback fabric navigation:', url);
          navigate(url);
        } else if (item_group && application) {
          const url = `/item_group/${encodeURIComponent(item_group)}/application/${encodeURIComponent(application)}`;
          console.log('Consumer fallback application navigation:', url);
          navigate(url);
        } else if (item_group) {
          const url = `/item_group/${encodeURIComponent(item_group)}`;
          console.log('Consumer fallback item group navigation:', url);
          navigate(url);
        } else {
          console.warn('Unable to determine navigation path for:', child);
        }
        break;
    }
  };

  // Handle scroll to section
  const handleScrollToSection = (child: ChildTileData) => {
    const itemGroupName = child.category_name || child.item_group_name || child.application;
    if (itemGroupName && onItemClick) {
      console.log('Scrolling to section:', itemGroupName);
      onItemClick(itemGroupName);
    }
  };

  // Main click handler - determines whether to scroll or navigate
  const handleItemClick = (child: ChildTileData) => {
    if (enableScrollMode && onItemClick) {
      handleScrollToSection(child);
    } else {
      handleNavigate(child);
    }
  };

  // Show missing content if no data
  if (!childrenData || childrenData.length === 0) {
    return <MissingContent sectionName="Applications Section" />;
  }

  // Filter out "wall decor" items and only show published items for consumer
  const filteredData = childrenData
    .filter(child => child.is_published) // Consumer only sees published items
    .filter(child => {
      const displayText = formatDisplayText(
        child.application || '',
        child.item_group_name || '',
        child.category_name || ''
      ).toLowerCase();
      return displayText !== 'wall decor';
    });

  // Sort data - respect parent sorting in scroll mode, otherwise sort alphabetically
  const processedData = enableScrollMode
    ? filteredData // Keep parent's sorting when in scroll mode
    : filteredData.sort((a, b) => {
      // Sort alphabetically by display text for navigation mode
      const textA = formatDisplayText(
        a.application || '',
        a.item_group_name || '',
        a.category_name || ''
      ).toLowerCase();
      const textB = formatDisplayText(
        b.application || '',
        b.item_group_name || '',
        b.category_name || ''
      ).toLowerCase();
      return textA.localeCompare(textB);
    });

  return (
    <Container maxWidth="xl">
      <Grid
        container
        rowSpacing={'16px'}
        columnSpacing={{ xs: 1, sm: 2, md: '16px' }}
        justifyContent="center"
        sx={{ mt: marginTop, mb: marginBottom }}
      >
        {processedData.map((child, index) => (
          <Grid key={child.category_id || index} item xs={12} sm={6} md={4} lg={spacingValue}>
            <Box
              sx={{
                position: 'relative',
                cursor: 'pointer',
                width: '100%',
                height: { xs: '320px', lg: '510px' },
                minHeight: '100%',
                overflow: 'hidden',
                '&:hover img': {
                  transform: 'scale(1.03)',
                },
              }}
              onClick={() => handleItemClick(child)}
            >
              {/* Image */}
              <img
                src={child.tile_display_media_single_link || '/placeholder-image.jpg'}
                loading="eager"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  transition: 'transform 0.5s ease',
                }}
                alt={child.category_name || child.application || 'Category'}
              />

              {/* Overlay with text */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'center',
                  background:
                    'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.36) 100%)',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    textAlign: 'center',
                    fontWeight: 'lighter',
                    color: '#fff',
                    fontSize: '40px',
                    textTransform: 'capitalize',
                    mb: { xs: 3, lg: 4 },
                  }}
                >
                  {formatDisplayText(
                    child.application || '',
                    child.item_group_name || '',
                    child.category_name || ''
                  )}
                </Typography>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default CMSConsumerApplicationsGrid;